import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { clientsApi } from "../api/clientsApi";
import { getApiErrorPayload } from "../utils/apiError";
import { isPlatformOwner } from "../utils/jwtUtils";
import type { Client } from "../types";

const ArchivedClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const platformOwner = isPlatformOwner(token);

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  useEffect(() => {
    if (!platformOwner) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await clientsApi.getArchived();
        if (!cancelled) setClients(data);
      } catch (err) {
        if (cancelled) return;
        const apiPayload = getApiErrorPayload(err);
        setError(apiPayload?.message ?? "Could not load archived clients.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [platformOwner]);

  const handleRestore = async (id: string) => {
    if (restoringId) return;
    setRestoringId(id);
    setError(null);
    try {
      await clientsApi.restore(id);
      setClients((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      const apiPayload = getApiErrorPayload(err);
      setError(apiPayload?.message ?? "Could not restore client.");
    } finally {
      setRestoringId(null);
    }
  };

  if (!platformOwner) {
    return (
      <DashboardLayout>
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-6">
          <h2 className="text-lg font-semibold text-white">Restricted</h2>
          <p className="mt-2 text-sm text-slate-400">
            The archived clients view is available to platform owners only.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-2xl font-semibold text-white">Archived clients</h1>
            <p className="mt-1 text-sm text-slate-400">
              Restore archived clients to bring them back to your active list.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/clients")}
            className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:bg-slate-800 hover:text-white"
          >
            ← Active clients
          </button>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-500/40 bg-red-950/40 p-3 text-sm text-red-200"
          >
            {error}
          </div>
        )}

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          {loading && (
            <p className="py-4 text-sm text-slate-400">Loading archived clients…</p>
          )}

          {!loading && clients.length === 0 && !error && (
            <div className="py-14 text-center">
              <p className="text-lg text-slate-200">No archived clients.</p>
              <p className="mt-1 text-sm text-slate-400">
                Archived clients will appear here when an operator archives them.
              </p>
            </div>
          )}

          {!loading && clients.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-400">
                    <th className="pb-3">Name</th>
                    <th className="pb-3">Email</th>
                    <th className="pb-3">Archived</th>
                    <th className="pb-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {clients.map((c) => (
                    <tr key={c.id} className="text-slate-200">
                      <td className="py-3 font-medium">
                        {c.firstName} {c.lastName}
                      </td>
                      <td className="py-3 text-slate-300">{c.email}</td>
                      <td className="py-3 text-slate-400">
                        {c.deletedAt
                          ? new Date(c.deletedAt).toLocaleString()
                          : "—"}
                      </td>
                      <td className="py-3 text-right">
                        <button
                          type="button"
                          onClick={() => void handleRestore(c.id)}
                          disabled={restoringId === c.id}
                          className="rounded-lg border border-emerald-900/50 px-3 py-1.5 text-xs font-medium text-emerald-300 transition hover:bg-emerald-950/40 hover:text-emerald-200 hover:border-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {restoringId === c.id ? "Restoring…" : "Restore"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ArchivedClientsPage;
